import FlashPCBSelect from "../common/FlashPCBSelect";
import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAppSelector } from "../../utils/hooks/store";
import settings from "../../constants/settings";
import useFetchAndTrace from "../../utils/hooks/fetchWithTrace";
import BoardOption from "../selectOptions/BoardOption";
import { v4 as uuidv4 } from "uuid";
import { useDebouncedCallback } from "use-debounce";
import { Board } from "../../reducers/uploadedBoard";

interface ImportableBoardOptionType {
  label: ReactNode;
  value: Board;
}

interface SelectBoardProps {
  selectedBoard: Board | null;
  setSelectedBoard: Dispatch<SetStateAction<Board | null>>;
  label?: string;
  placeholder?: string;
  doNotIncludeBoardId?: string;
  orderedBoardsOnly?: boolean;
}

export default function SelectBoard({
  selectedBoard,
  setSelectedBoard,
  label,
  placeholder,
  doNotIncludeBoardId,
  orderedBoardsOnly,
}: SelectBoardProps) {
  const fetchAndTrace = useFetchAndTrace();
  const [loading, setLoading] = useState(false);
  const { userId } = useAppSelector((state) => state.auth);
  const [inputValue, setInputValue] = useState("");
  const [importableBoards, setImportableBoards] = useState<Board[]>([]);
  const [nextPageToFetch, setNextPageToFetch] = useState(1);
  const getBoardsRequestIdRef = useRef<string>(uuidv4());
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const loadBoardsHandler = useCallback(
    (requestId: string, append?: boolean) => {
      return async (response: Response) => {
        if (requestId == getBoardsRequestIdRef.current) {
          if (response.ok) {
            const boardsResponse = (await response.json()) as {
              boards: Board[];
            };
            setImportableBoards((prevState) => [
              ...(append ? prevState : []),
              ...boardsResponse.boards,
            ]);
            setNextPageToFetch((prevPage) => prevPage + 1);
            setLoading(false);
          } else {
            throw Error("Failed to get all boards");
          }
        }
      };
    },
    [],
  );

  const loadBoards = useDebouncedCallback(
    (requestId: string, append?: boolean) => {
      const orderedBoardsOnlyQuery = orderedBoardsOnly
        ? "&ordered_boards_only=true"
        : "";
      setLoading(true);
      void fetchAndTrace(
        `${settings.api_url}/boards/search?page=${nextPageToFetch}${orderedBoardsOnlyQuery}`,
        loadBoardsHandler(requestId, append),
        null,
        {
          body: JSON.stringify({
            search_term: inputValue,
          }),
          method: "PUT",
        },
      );
    },
    300,
  );

  function onSelectBoard(newValue: ImportableBoardOptionType | null) {
    setImportableBoards([]);
    setSelectedBoard(newValue?.value || null);
  }

  useEffect(() => {
    getBoardsRequestIdRef.current = uuidv4();
    setLoading(true);
    if (!inputValue) {
      setImportableBoards([]);
    }
    setNextPageToFetch(1);
    loadBoards(getBoardsRequestIdRef.current);
  }, [inputValue, loadBoards, selectedBoard]);

  const getValue = () => {
    // In the case that the selected id is null we return null so that the field is cleared
    if (selectedBoard === null) {
      return null;
    }
    // Otherwise, we allow undefined which allows for a less jumpy experience as the userResponse loads
    return (
      selectedBoard && {
        value: selectedBoard,
        label: <>{selectedBoard.project_name}</>,
      }
    );
  };

  return (
    <FlashPCBSelect
      label={label}
      name="Boards"
      isLoading={loading && menuIsOpen}
      onMenuClose={() => setMenuIsOpen(false)}
      onMenuOpen={() => setMenuIsOpen(true)}
      loadingMessage={() => "Searching boards..."}
      placeholder={placeholder}
      isClearable
      inputValue={inputValue}
      onInputChange={setInputValue}
      value={getValue()}
      options={importableBoards?.map((board) => {
        return {
          value: board,
          label: (
            <BoardOption
              board={board}
              currentUserId={userId}
              selected={board.id == selectedBoard?.id}
            />
          ),
        };
      })}
      isOptionDisabled={({ value: boardOption }) => {
        return boardOption.id == doNotIncludeBoardId;
      }}
      noOptionsMessage={() => "No boards found"}
      onMenuScrollToBottom={() =>
        loadBoards(getBoardsRequestIdRef.current, true)
      }
      onChange={onSelectBoard}
      filterOption={() => {
        return true;
      }}
    />
  );
}
